'use client';

import { Trans, t } from '@lingui/macro';
import { Box, Container, Stack, Typography } from '@mui/material';
import { Button, PageHeader } from '@prismo-io/design-system';
import Image from 'next/image';
import Link from 'next/link';
import type { FC } from 'react';

import auraImg from '@/assets/aura/app/no-result.svg';
import DefaultAuraLogo from '@/assets/aura/app/primary/common/icone-aura.svg';
import backgroundImg from '@/assets/aura/backgrounds/no-result.png';
import { useLingui } from '@lingui/react';
import { Application, translate } from '@prismo-io/router';
import { LanguageCode, type LanguageCodeT } from '@prismo-io/schemas';

type Props = unknown;

const buildQuestionnaireUrl = (locale: LanguageCodeT) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.AURA,
    path: '/',
    addHostname: true,
    locale,
  });
};

export const AuraNoResult: FC<Props> = () => {
  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;
  const questionnaireUrl = buildQuestionnaireUrl(locale as LanguageCodeT);

  const auraTitle = (
    <>
      <Typography
        color="white"
        fontSize={{ none: '1.10rem', sm: '1.25rem', md: '1.75rem' }}
        fontWeight="bolder"
      >
        Aura
      </Typography>
      <Typography
        color="white"
        fontSize={{ none: '1.10rem', sm: '1.25rem', md: '1.75rem' }}
      >
        |
      </Typography>
      <Typography
        color="white"
        fontSize={{ none: '1.10rem', sm: '1.25rem', md: '1.75rem' }}
      >
        {t`Comportement`}
      </Typography>
    </>
  );

  return (
    <Box
      position="relative"
      width="100%"
      height="100vh"
      component="section"
      overflow="hidden"
    >
      <Box
        component="div"
        position="absolute"
        top="0"
        bottom="0"
        right="0"
        left="0"
      >
        <Image
          src={backgroundImg}
          alt=""
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          fill
        />
      </Box>
      <PageHeader pageImg={DefaultAuraLogo} pageTitleRender={auraTitle} />
      <Box
        component="div"
        height="100%"
        py={{ none: '1.5rem', sm: '2rem', lg: '4rem', xl: '6rem' }}
        overflow="auto"
      >
        <Container maxWidth="sm">
          <Stack
            position="relative"
            direction="column"
            alignItems="center"
            spacing={4}
          >
            <Box
              component="div"
              position="relative"
              width="100%"
              height={{ none: '10rem', sm: '15rem' }}
            >
              <Image
                src={auraImg}
                alt=""
                style={{ objectFit: 'contain', objectPosition: 'center' }}
                fill
              />
            </Box>
            <Typography
              variant="h2"
              fontSize="1.5rem"
              lineHeight="2rem"
              textAlign="center"
              color="primary.main"
            >
              <Trans>Vous aussi découvrez votre profil comportemental !</Trans>
            </Typography>
            <Stack justifyContent="center">
              <Button
                LinkComponent={Link}
                href={questionnaireUrl}
                variant="contained"
                color="gradient"
                sx={{
                  '& .MuiButton-startIcon': {
                    display: 'none',
                  },
                }}
              >
                <Trans>Passez Aura dès maintenant</Trans>
              </Button>
            </Stack>
            <Typography
              fontSize="1.5rem"
              color="white"
              textAlign="center"
              lineHeight="2rem"
            >
              <Trans>
                40 questions et 5 min seulement pour faire le point sur votre
                mode de communication.
              </Trans>
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};
